import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { hasRoles, requiredRolesForPath } from 'utils/accessPolicy'
import { getMe } from 'service/AdminPanelService'
import { setUser, setLoading, clearUser } from 'store/slices/AuthSlice'
import UnvLayout from 'components/Layouts/UnvLayout/UnvLayout'
import { Spinner } from 'components/UnvUI'
import tokenDecode from 'utils/decode'
import { refreshAccessToken } from 'http/authInterceptor'

const readToken = () => {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem('token')
  } catch (_) {
    return null
  }
}

function ProtectedRoute({ children, requireRoles, bare }) {
  const location = useLocation()
  const dispatch = useDispatch()
  const auth = useSelector((state) => state.auth)

  // Token из state, чтобы реагировать на изменения (logout в другой вкладке).
  const [token, setToken] = useState(() => readToken())
  const [refreshing, setRefreshing] = useState(false)
  const [meError, setMeError] = useState(false)

  // Подписка на storage event — logout в другой вкладке должен выкинуть и тут.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const onStorage = (e) => {
      if (e.key === 'token' || e.key === null) {
        setToken(readToken())
      }
    }
    window.addEventListener('storage', onStorage)
    const onTokenChanged = () => setToken(readToken())
    window.addEventListener('chu:auth-token', onTokenChanged)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('chu:auth-token', onTokenChanged)
    }
  }, [])

  // JWT-валидация: decode.js возвращает null при истёкшем exp / битом токене.
  const tokenValid = useMemo(() => {
    if (!token) return false
    return tokenDecode(token) !== null
  }, [token])

  useEffect(() => {
    if (!token || tokenValid) { setRefreshing(false); return undefined }
    let cancelled = false
    if (!localStorage.getItem('refresh')) { setToken(null); return undefined }
    setRefreshing(true)
    refreshAccessToken().then((access) => { if (!cancelled) setToken(access) }).catch((error) => {
      if (cancelled) return
      if (!error?.response || error.response.status >= 500 || error.response.status === 429) {
        setMeError(true)
        setRefreshing(false)
        return
      }
      localStorage.removeItem('token')
      localStorage.removeItem('refresh')
      dispatch(clearUser())
      setToken(null)
      setRefreshing(false)
    })
    return () => { cancelled = true }
  }, [token, tokenValid, dispatch])

  const fetchMe = useCallback(() => {
    setMeError(false)
    dispatch(setLoading(true))
    getMe()
      .then((res) => {
        dispatch(setUser(res?.data || res))
      })
      .catch((error) => {
        if (!error?.response || error.response.status >= 500 || error.response.status === 429) {
          setMeError(true)
          dispatch(setLoading(false))
          return
        }
        // Ошибка получения /me — выкидываем пользователя из системы.
        dispatch(clearUser())
        dispatch(setLoading(false))
        try {
          localStorage.removeItem('token')
          setToken(null)
        } catch (_) {
          /* ignore */
        }
      })
  }, [dispatch])

  useEffect(() => {
    if (!tokenValid) return
    if (auth?.loaded || auth?.loading || meError) return
    fetchMe()
  }, [tokenValid, auth?.loaded, auth?.loading, meError, fetchMe])

  if (meError) return <div className="unv-card" role="alert" style={{ margin: '32px auto', maxWidth: 460, padding: 28 }}><p>Сервис временно недоступен. Повторите подключение.</p><button className="unv-btn" onClick={fetchMe}>Повторить</button></div>

  // Истёкший токен обновляем до перехода на страницу входа.
  if (token && !tokenValid && (refreshing || localStorage.getItem('refresh'))) return <Spinner.Center />
  if (!token || !tokenValid) {
    return <Navigate to="/login" replace />
  }

  // Пока подгружается профиль — показываем spinner вместо пустого экрана.
  if (auth?.loading || (!auth?.loaded && !auth?.me)) {
    return <Spinner.Center />
  }

  // is_locked из MeView — отправляем на страницу разблокировки.
  if (auth?.loaded && (auth?.isLocked || auth?.me?.is_locked)) {
    return <Navigate to="/account-locked/" replace />
  }

  const scopedRoles = requiredRolesForPath(location.pathname) || requireRoles
  if (Array.isArray(scopedRoles) && scopedRoles.length > 0) {
    if (!auth?.loaded) {
      return <Spinner.Center />
    }
    const isSuperuser = !!(auth?.me?.is_superuser)
    const userRoleCodes = (auth?.roles || []).map((r) =>
      typeof r === 'string' ? r : r?.code,
    )
    const allowed = hasRoles(userRoleCodes, scopedRoles, isSuperuser)
    if (!allowed) {
      // Если у пользователя есть хоть какая-то роль — отправляем на /home/.
      // Иначе на /login/, чтобы не было бесконечных редиректов.
      const target = userRoleCodes.length > 0 ? '/home/' : '/login'
      return <Navigate to={target} replace />
    }
  }

  // По умолчанию заворачиваем страницу в общий шелл (sidebar + topbar).
  // bare=true оставляет страницу без обёртки (для full-screen экранов
  // вроде /post-login/).
  if (bare) return children
  return <UnvLayout>{children}</UnvLayout>
}

export default ProtectedRoute
